import AlertIcons from 'components/AlertIcons';
import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import AlertSlider from 'components/Sliders/AlertSlider';
import { nwsAlertTypes } from 'features/Weather/features/WeatherAlerts/config/nwsAlertTypes.config';
import {
  getAlertSeverityColor,
  getAlertSeverityLabel,
} from 'features/Weather/features/WeatherAlerts/utils/alertSeverity.utils';
import { useEffect } from 'react';
import { buildDeviceSelectOptionsForDeviceType } from '../../../conditions/DeviceCondition/utils/deviceCondition.utils';
import RadialAlertMap from './RadialAlertMap';

const alertTypeOptions = nwsAlertTypes.map((type) => ({ value: type, label: type }));

export function VerifyRadialAlert(dataObj) {
  if (!dataObj) return false;
  return !(
    dataObj?.headline === '' ||
    dataObj?.description === '' ||
    dataObj?.type === '' ||
    dataObj?.radius === '' ||
    dataObj?.severity === ''
  );
}

export default function CreateRadialAlert({ onUpdate, deviceList, dataObj, readOnly = false }) {
  const deviceOptions = buildDeviceSelectOptionsForDeviceType(deviceList, dataObj.deviceType);

  useEffect(() => {
    if (
      !dataObj?.headline ||
      !dataObj?.description ||
      !dataObj?.type ||
      !dataObj?.radius ||
      !dataObj?.severity
    ) {
      onUpdate({
        headline: dataObj?.headline ?? '',
        description: dataObj?.description ?? '',
        type: dataObj?.type ?? '',
        radius: dataObj?.radius ?? 250,
        severity: dataObj?.severity ?? 25,
        isInternalUse: dataObj?.isInternalUse ?? false,
      });
    }
  }, []);

  const aliasLocked =
    readOnly &&
    !!dataObj.node_id &&
    !deviceList.some((d) => d.node_id?.toString() === dataObj.node_id?.toString());

  return (
    <div className="global-action-builder-row container-fluid p-3 rounded-2 m-0">
      <div className="row g-3 align-items-stretch">
        {/* Map */}
        <div className="col-12 col-md-6 d-flex">
          <div
            className="flex-fill rounded-2 shadow overflow-hidden"
            style={{ height: 'clamp(280px, 50vh, 600px)' }}
          >
            <RadialAlertMap
              arrayOfDevices={deviceList}
              selectedDevice={dataObj.selectedDevice}
              setSelectedDevice={(nodeId) => {
                const device = deviceList.find((d) => d.node_id === nodeId);
                if (device) {
                  const deviceCopy = { ...device, data: [device.data[0]] };
                  onUpdate({
                    node_id: nodeId,
                    selectedDevice: deviceCopy,
                    coordinates: [device.lat, device.lon],
                  });
                }
              }}
              radius={Number(dataObj.radius) || 0}
            />
          </div>
        </div>

        {/* Form */}
        <div className="col-12 col-md-6 d-flex">
          <div className="flex-fill d-flex flex-column gap-3">
            {/* Device Alias */}
            <BaseSelect
              label="Device Alias:"
              wrapperClassName="flex-fill"
              isSearchable
              isClearable
              classNames={{
                control: () => 'global-action-builder-field',
              }}
              selectProps={{
                options: deviceOptions,
                value: findSelectOption(deviceOptions, dataObj.node_id),
                placeholder: 'Select',
                isDisabled: readOnly || aliasLocked,
                onChange: (option) => {
                  const device = option?.device;
                  onUpdate({
                    node_id: option?.value ?? '',
                    selectedDevice: device ? { ...device, data: [device.data[0]] } : null,
                    coordinates: [device?.coordinates?.lat, device?.coordinates?.lng],
                  });
                },
              }}
            />
            {/* Headline */}
            <div className="d-flex flex-column">
              <div className="mb-1 fw-medium">Headline:</div>
              <input
                type="text"
                name="headline"
                className="global-action-builder-field p-1 rounded-1 w-100"
                onChange={(e) => onUpdate({ headline: e.target.value })}
                value={dataObj.headline || ''}
              />
            </div>
            {/* Description */}
            <div className="d-flex flex-column">
              <div className="mb-1 fw-medium">Description:</div>
              <textarea
                name="description"
                className="global-action-builder-field p-1 rounded-1 w-100"
                rows={4}
                onChange={(e) => onUpdate({ description: e.target.value })}
                value={dataObj.description || ''}
              />
            </div>
            {/* Type */}
            <div className="d-flex flex-column">
              <div className="mb-1 fw-medium d-flex align-items-center gap-2">
                <span>Type:</span>
                <span className="d-inline-flex">
                  <AlertIcons type={dataObj.type} />
                </span>
              </div>
              <BaseSelect
                isSearchable
                classNames={{ control: () => 'global-action-builder-field' }}
                selectProps={{
                  'aria-label': 'Alert Type',
                  options: alertTypeOptions,
                  value: findSelectOption(alertTypeOptions, dataObj.type),
                  placeholder: '-Select-',
                  isDisabled: readOnly,
                  onChange: (option) => onUpdate({ type: option?.value ?? '' }),
                }}
              />
            </div>
            {/* Radius */}
            <div className="d-flex flex-column">
              <div className="mb-1 fw-medium">Radius (ft):</div>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="number"
                  name="radius"
                  className="global-action-builder-field p-1 rounded-1"
                  style={{ width: 100 }}
                  onChange={(e) =>
                    onUpdate({
                      radius: e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                  value={dataObj.radius ?? ''}
                />
                <span className="text-body-secondary">ft</span>
              </div>
            </div>
            {/* Severity */}
            <div className="d-flex flex-column">
              <div
                className="px-2 py-1 rounded-1 mb-2"
                style={{
                  width: 'max-content',
                  background: getAlertSeverityColor(dataObj.severity),
                }}
              >
                Severity: {getAlertSeverityLabel(dataObj.severity)}
              </div>
              {/* slider in a minimal box */}
              <div className="global-action-builder-editor px-2 rounded-2" style={{ minWidth: 0 }}>
                <AlertSlider
                  value={dataObj.severity ?? 25}
                  min={0}
                  max={99}
                  step={1}
                  onChange={(e) => onUpdate({ severity: Number(e.target.value) })}
                />
              </div>
            </div>
            {/* Internal Use Only */}
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="radial-alert-internal-use"
                checked={!!dataObj.isInternalUse}
                disabled={readOnly}
                onChange={(e) => onUpdate({ isInternalUse: e.target.checked })}
              />
              <label className="form-check-label fw-medium" htmlFor="radial-alert-internal-use">
                Internal Use Only
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
